import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, MenuItem, TextField,
} from '@mui/material';
import RecyclingIcon from '@mui/icons-material/Recycling';
import ReactECharts from 'echarts-for-react';
import { v5Service } from '../services/v5Service';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

function indicatorChips(indicators) {
  if (!indicators || !indicators.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {indicators.map((i) => (
        <Chip key={i.code} size="small" variant="outlined"
          label={`${i.code} ${Number(i.value).toFixed(1)}`}
          title={`${i.name}: ${Number(i.value).toFixed(1)}`} />
      ))}
    </Box>
  );
}

function StatCard({ title, value, suffix }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {value}{suffix && <Typography component="span" variant="subtitle2" color="text.secondary"> {suffix}</Typography>}
        </Typography>
      </CardContent>
    </Card>
  );
}

function Circularity() {
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState('');
  const [statements, setStatements] = useState([]);
  const [flows, setFlows] = useState([]);
  const [eol, setEol] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [checks, setChecks] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [summary, ledgersData] = await Promise.all([
        v5Service.getCircularitySummary(),
        v5Service.getLedgers(),
      ]);
      setOrgs(summary);
      setLedgers(ledgersData);
      const [stmts, flowRows, eolRows, facilityRows, checkRows] = await Promise.all([
        v5Service.getCircularityStatements(orgId || null),
        v5Service.getCircularityFlows(orgId || null),
        v5Service.getEndOfLifeFlows(orgId || null),
        v5Service.getCircularityByFacility(orgId || null),
        v5Service.getConservationChecks(),
      ]);
      setStatements(stmts);
      setFlows(flowRows);
      setEol(eolRows);
      setFacilities(facilityRows);
      setChecks(checkRows.filter((c) => !orgId || c.organization_name === (summary.find((o) => o.organization_id === orgId) || {}).organization_name));
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const totals = orgs.reduce((acc, o) => ({
    input: acc.input + Number(o.input_kg),
    circularIn: acc.circularIn + Number(o.circular_in_kg),
    output: acc.output + Number(o.output_kg),
    circularOut: acc.circularOut + Number(o.circular_out_kg),
    waste: acc.waste + Number(o.waste_kg),
  }), { input: 0, circularIn: 0, output: 0, circularOut: 0, waste: 0 });
  const meanOf = (key) => {
    const vals = orgs.map((o) => Number(o[key] || 0)).filter((v) => v > 0);
    return vals.length ? vals.reduce((a, v) => a + v, 0) / vals.length : 0;
  };

  // ── chart data ─────────────────────────────────────────
  const chartOrgs = orgId ? orgs.filter((o) => o.organization_id === orgId) : orgs;
  const flowChartOption = {
    tooltip: {
      trigger: 'axis',
      valueFormatter: (v) => `${Number(v).toLocaleString()} kg`,
    },
    legend: { bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 8, right: 8, top: 24, bottom: 40, containLabel: true },
    xAxis: {
      type: 'category',
      data: chartOrgs.map((o) => o.organization_name),
      axisLabel: { fontSize: 10, interval: 0, rotate: 18 },
    },
    yAxis: { type: 'value', axisLabel: { formatter: (v) => `${Math.round(v / 1000)}t` } },
    series: [
      { name: 'Circular in', type: 'bar', stack: 'input', itemStyle: { color: '#2e7d32' }, data: chartOrgs.map((o) => Number(o.circular_in_kg || 0)) },
      { name: 'Linear in', type: 'bar', stack: 'input', itemStyle: { color: '#a5d6a7' }, data: chartOrgs.map((o) => Number(o.input_kg || 0) - Number(o.circular_in_kg || 0)) },
      { name: 'Circular out', type: 'bar', stack: 'output', itemStyle: { color: '#1565c0' }, data: chartOrgs.map((o) => Number(o.circular_out_kg || 0)) },
      { name: 'Linear out', type: 'bar', stack: 'output', itemStyle: { color: '#90caf9' }, data: chartOrgs.map((o) => Number(o.output_kg || 0) - Number(o.circular_out_kg || 0)) },
    ],
  };

  const eolByFate = {};
  eol.forEach((e) => {
    eolByFate[e.fate_name] = eolByFate[e.fate_name] || { value: 0, circular: Boolean(e.is_circular) };
    eolByFate[e.fate_name].value += Number(e.mass_amount || 0);
  });
  const CIRCULAR_FATE_COLORS = ['#2e7d32', '#00897b', '#558b2f', '#00695c', '#43a047'];
  const LINEAR_FATE_COLORS = ['#9e9e9e', '#ef6c00', '#6d4c41', '#757575'];
  let circularIdx = 0;
  let linearIdx = 0;
  const eolChartData = Object.entries(eolByFate).map(([name, d]) => ({
    name,
    value: Math.round(d.value),
    itemStyle: {
      color: d.circular
        ? CIRCULAR_FATE_COLORS[circularIdx++ % CIRCULAR_FATE_COLORS.length]
        : LINEAR_FATE_COLORS[linearIdx++ % LINEAR_FATE_COLORS.length],
    },
  }));
  const eolChartOption = {
    tooltip: {
      trigger: 'item',
      valueFormatter: (v) => `${Number(v).toLocaleString()} kg`,
    },
    legend: { bottom: 0, textStyle: { fontSize: 11 }, type: 'scroll' },
    series: [{
      type: 'pie',
      radius: ['42%', '68%'],
      center: ['50%', '42%'],
      label: { formatter: '{b}\n{d}%', fontSize: 10 },
      data: eolChartData,
    }],
  };

  // Clicking a bar toggles the organization filter onto that bar's organization.
  // The org list is read through a ref so the handler stays referentially stable
  // even if echarts binds it only once at chart mount.
  const orgsRef = useRef(orgs);
  useEffect(() => { orgsRef.current = orgs; }, [orgs]);
  const handleFlowChartClick = useCallback((params) => {
    const org = orgsRef.current.find((o) => o.organization_name === params.name);
    if (!org) return;
    setOrgId((prev) => (prev === org.organization_id ? '' : org.organization_id));
  }, []);

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <RecyclingIcon color="primary" />
        <Typography variant="h4">Circularity</Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" paragraph>
        v5 circular flow statements, indicators (WBCSD CTI / GCP), material flows and end-of-life fates from the Open Footprint database
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
        <TextField
          select label="Organization" value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          sx={{ minWidth: 260 }} size="small"
        >
          <MenuItem value="">All organizations</MenuItem>
          {orgs.map((o) => (
            <MenuItem key={o.organization_id} value={o.organization_id}>{o.organization_name}</MenuItem>
          ))}
        </TextField>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}><StatCard title="Total material input" value={fmt(totals.input)} suffix="kg" /></Grid>
        <Grid item xs={6} md={3}><StatCard title="Total material output" value={fmt(totals.output)} suffix="kg" /></Grid>
        <Grid item xs={6} md={3}><StatCard title="Avg circular inflow rate (CR_IN)" value={meanOf('avg_cr_in').toFixed(1)} suffix="%" /></Grid>
        <Grid item xs={6} md={3}><StatCard title="Avg recycling rate (RR)" value={meanOf('avg_rr').toFixed(1)} suffix="%" /></Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={7}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Circular vs Linear Flows (kg)
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                (click a bar to focus an organization)
              </Typography>
            </Typography>
            <ReactECharts option={flowChartOption} style={{ height: 300 }} onEvents={{ click: handleFlowChartClick }} />
          </Paper>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>End-of-Life Fate Mix</Typography>
            <ReactECharts option={eolChartOption} style={{ height: 300 }} />
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ p: 2, pb: 1 }}>Circular Flow Statements</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {!orgId && <TableCell>Organization</TableCell>}
                <TableCell>Facility</TableCell>
                <TableCell>Period</TableCell>
                <TableCell align="right">Input (kg)</TableCell>
                <TableCell align="right">Output (kg)</TableCell>
                <TableCell align="right">Circular in (kg)</TableCell>
                <TableCell align="right">Circular out (kg)</TableCell>
                <TableCell align="right">Waste (kg)</TableCell>
                <TableCell>Indicators</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {statements.map((s) => (
                <TableRow key={s.circular_flow_statement_id} hover>
                  {!orgId && <TableCell>{s.organization_name}</TableCell>}
                  <TableCell>{s.facility_name}</TableCell>
                  <TableCell>{s.reporting_period_start} → {s.reporting_period_end}</TableCell>
                  <TableCell align="right">{fmt(s.total_material_input_kg)}</TableCell>
                  <TableCell align="right">{fmt(s.total_material_output_kg)}</TableCell>
                  <TableCell align="right">{fmt(s.circular_flow_in_kg)}</TableCell>
                  <TableCell align="right">{fmt(s.circular_flow_out_kg)}</TableCell>
                  <TableCell align="right">{fmt(s.waste_generated_kg)}</TableCell>
                  <TableCell>{indicatorChips(s.indicators)}</TableCell>
                </TableRow>
              ))}
              {!statements.length && (
                <TableRow><TableCell colSpan={9}>No circular flow statements found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ p: 2, pb: 1 }}>By Facility</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {!orgId && <TableCell>Organization</TableCell>}
                <TableCell>Facility</TableCell>
                <TableCell align="right">Statements</TableCell>
                <TableCell align="right">Input (kg)</TableCell>
                <TableCell align="right">Circular in (kg)</TableCell>
                <TableCell align="right">Output (kg)</TableCell>
                <TableCell align="right">Circular out (kg)</TableCell>
                <TableCell align="right">Waste (kg)</TableCell>
                <TableCell align="right">CR_IN</TableCell>
                <TableCell align="right">RR</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {facilities.map((f) => (
                <TableRow key={f.facility_id} hover>
                  {!orgId && <TableCell>{f.organization_name}</TableCell>}
                  <TableCell>{f.facility_name}</TableCell>
                  <TableCell align="right">{f.statements}</TableCell>
                  <TableCell align="right">{fmt(f.input_kg)}</TableCell>
                  <TableCell align="right">{fmt(f.circular_in_kg)}</TableCell>
                  <TableCell align="right">{fmt(f.output_kg)}</TableCell>
                  <TableCell align="right">{fmt(f.circular_out_kg)}</TableCell>
                  <TableCell align="right">{fmt(f.waste_kg)}</TableCell>
                  <TableCell align="right">{f.avg_cr_in ?? '—'}</TableCell>
                  <TableCell align="right">{f.avg_rr ?? '—'}</TableCell>
                </TableRow>
              ))}
              {!facilities.length && (
                <TableRow><TableCell colSpan={10}>No facility data found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Paper>
            <Typography variant="h6" sx={{ p: 2, pb: 1 }}>Material Flows</Typography>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {!orgId && <TableCell>Organization</TableCell>}
                    <TableCell>Facility</TableCell>
                    <TableCell>Material</TableCell>
                    <TableCell>Flow type</TableCell>
                    <TableCell>Direction</TableCell>
                    <TableCell align="right">Mass</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {flows.map((f) => (
                    <TableRow key={f.material_flow_id} hover>
                      {!orgId && <TableCell>{f.organization_name}</TableCell>}
                      <TableCell>{f.facility_name}</TableCell>
                      <TableCell>{f.material_name}</TableCell>
                      <TableCell>{f.flow_type_name}</TableCell>
                      <TableCell>
                        <Chip size="small" color={f.flow_direction === 'inflow' ? 'primary' : 'default'}
                          variant="outlined" label={f.flow_direction} />
                      </TableCell>
                      <TableCell align="right">{fmt(f.mass_amount)} {f.unit_code}</TableCell>
                    </TableRow>
                  ))}
                  {!flows.length && (
                    <TableRow><TableCell colSpan={6}>No material flows found.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ p: 2, pb: 1 }}>End-of-Life Fates</Typography>
            <TableContainer sx={{ maxHeight: 200 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {!orgId && <TableCell>Organization</TableCell>}
                    <TableCell>Material</TableCell>
                    <TableCell>Fate</TableCell>
                    <TableCell align="right">Mass (kg)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {eol.map((e) => (
                    <TableRow key={e.end_of_life_flow_id} hover>
                      {!orgId && <TableCell>{e.organization_name}</TableCell>}
                      <TableCell>{e.material_name}</TableCell>
                      <TableCell>
                        <Chip size="small" color={e.is_circular ? 'success' : 'default'} variant="outlined" label={e.fate_name} />
                      </TableCell>
                      <TableCell align="right">{fmt(e.mass_amount)}</TableCell>
                    </TableRow>
                  ))}
                  {!eol.length && (
                    <TableRow><TableCell colSpan={4}>No end-of-life flows found.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper>
            <Typography variant="h6" sx={{ p: 2, pb: 1 }}>v5 Ledgers (kg CO₂e)</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ledger</TableCell>
                    <TableCell align="right">Debit</TableCell>
                    <TableCell align="right">Credit</TableCell>
                    <TableCell align="right">Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ledgers
                    .filter((l) => !orgId || l.entity_id === orgId)
                    .map((l) => (
                      <TableRow key={l.e_ledger_id} hover>
                        <TableCell>{l.ledger_name}</TableCell>
                        <TableCell align="right">{fmt(l.total_debit_kg_co2e)}</TableCell>
                        <TableCell align="right">{fmt(l.total_credit_kg_co2e)}</TableCell>
                        <TableCell align="right">{fmt(l.balance_kg_co2e)}</TableCell>
                      </TableRow>
                    ))}
                  {!ledgers.filter((l) => !orgId || l.entity_id === orgId).length && (
                    <TableRow><TableCell colSpan={4}>No v5 ledgers found.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <Paper>
        <Typography variant="h6" sx={{ p: 2, pb: 1 }}>Conservation Checks (v5)</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1 }}>
          Mass balance across the v5 e-ledgers — net accumulated quantity must equal the expected accumulation
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Organization</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>Period</TableCell>
                <TableCell align="right">Inflow</TableCell>
                <TableCell align="right">Outflow</TableCell>
                <TableCell align="right">Net accumulated</TableCell>
                <TableCell align="right">Expected</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Balance check</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {checks.map((c) => (
                <TableRow key={c.check_id} hover>
                  <TableCell>{c.organization_name || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={c.resource_type}
                      color={c.resource_type === 'material' ? 'primary' : 'info'} />
                  </TableCell>
                  <TableCell>{c.reporting_period_start} → {c.reporting_period_end}</TableCell>
                  <TableCell align="right">{fmt(c.total_inflow_quantity)}</TableCell>
                  <TableCell align="right">{fmt(c.total_outflow_quantity)}</TableCell>
                  <TableCell align="right">{fmt(c.net_accumulated_quantity)}</TableCell>
                  <TableCell align="right">{fmt(c.expected_accumulated_quantity)}</TableCell>
                  <TableCell>{(c.unit_of_measure_id || '').replace('uom-', '')}</TableCell>
                  <TableCell>
                    <Chip size="small" color={c.conservation_holds ? 'success' : 'error'}
                      label={c.conservation_holds ? 'Holds' : 'Violation'} />
                  </TableCell>
                </TableRow>
              ))}
              {!checks.length && (
                <TableRow><TableCell colSpan={9}>No conservation checks found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default Circularity;
